import React, { useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { GiCarDoor } from "react-icons/gi";
import { FaUserFriends, FaSuitcaseRolling, FaCogs, FaCarSide } from "react-icons/fa";
import { useNavigate } from "react-router-dom";


function Fleet() {
  const [allCars, setAllCars] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate=useNavigate()

  // const filters = ["All", "Sedan", "SUV", "Luxury", "Van", "Truck"];
  const filters = ["All", "Sedan", "SUV", "Van"];

  useEffect(() => {
    async function fetchFleetData() {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseUrl}fleet_details`, { method: "POST" });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const data = await response.json();
        const desiredOrder = [
          "Economy", "Compact", "Intermediate", "Compact SUV", 
          "Standard SUV", "Full Size SUV", "Premium SUV", "Mini Van", "Luxury"
        ];

        const carsArray = desiredOrder
          .filter((name) => data[name])
          .map((name) => {
            const details = data[name];
            const bagsCount = details.luggage_count?.split("+").map(x => parseInt(x.trim())).reduce((a, b) => a + b, 0) || "N/A";
            
            // Basic categorization logic
            let type = "Sedan"; 
            if (name.toLowerCase().includes("suv")) type = "SUV";
            else if (name.toLowerCase().includes("van")) type = "Van";
            else if (name.toLowerCase().includes("luxury")) type = "Luxury";
            else if (name.toLowerCase().includes("truck") || name.toLowerCase().includes("pickup")) type = "Truck";

            return {
              name,
              type,
              image: details.image_path,
              seats: details.seat_count || "N/A",
              bags: bagsCount,
              doors: details.door_count || "N/A",
              transmission: "Automatic",
            };
          });

        setAllCars(carsArray);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }
    fetchFleetData();
  }, []);

  const filteredCars = useMemo(() => {
    if (activeFilter === "All") return allCars;
    return allCars.filter(car => car.type === activeFilter);
  }, [activeFilter, allCars]);

  if (loading) return <div className="bg-[#006039] py-20 text-center text-white">Loading Fleet...</div>;
  if (error) return <div className="bg-[#006039] py-20 text-center text-red-400">Error: {error}</div>;

  return (
    <section className="bg-[#006039] py-16 px-4 min-h-[600px]">
      <div className="max-w-7xl mx-auto text-center mb-10">
        <h2 className="text-4xl  text-white mb-2">Our Fleet</h2>
        <p className="text-gray-200 mb-8">Choose from our wide range of vehicles</p>

        {/* Filter Bar */}
     {/* Filter Bar */}
<div className="flex flex-wrap justify-center gap-3 mb-12">
  {filters.map((f) => (
    <button
      key={f}
      onClick={() => setActiveFilter(f)}
      className={`px-8 py-2 rounded-full text-sm font-semibold transition-all duration-300 shadow-md border transform hover:scale-105 active:scale-95 ${
        activeFilter === f 
        ? "bg-[#ca8032] text-white border-[#ca8032] shadow-[#ca8032]/40" 
        : "bg-white text-gray-700 border-transparent hover:bg-[#ca8032] hover:text-white hover:shadow-lg hover:shadow-[#ca8032]/20"
      }`}
    >
      {f}
    </button>
  ))}
</div>
      </div>

      <div className="max-w-7xl mx-auto relative group">
        {filteredCars.length > 0 ? (
          <Swiper
            key={activeFilter}
            modules={[Navigation, Pagination]}
            spaceBetween={20}
            slidesPerView={1}
            navigation={{ nextEl: ".fleet-next", prevEl: ".fleet-prev" }}
            breakpoints={{ 
              640: { slidesPerView: 2 }, 
              1024: { slidesPerView: 3 },
              1280: { slidesPerView: 3 } 
            }}
            className="pb-12"
          >
            {filteredCars.map((car, i) => (
              <SwiperSlide key={i}>
                <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full flex flex-col">
                  {/* Reduced space image container */}
                  <div className="bg-white px-6 pt-6 pb-2 h-44 flex items-center justify-center">
                    <img src={car.image} alt={car.name} className="max-w-full max-h-full object-contain" />
                  </div>

                  <div className="px-6 pb-6 flex-grow flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{car.name}</h3>
                    
                    <div className="grid grid-cols-2 gap-y-3 gap-x-2 border-t pt-4 border-gray-100 mb-6">
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaUserFriends className="text-[#ca8032]" />
                        <span className="text-sm font-medium">{car.seats} Seats</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaSuitcaseRolling className="text-[#ca8032]" />
                        <span className="text-sm font-medium">{car.bags} Bags</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <GiCarDoor className="text-[#ca8032]" />
                        <span className="text-sm font-medium">{car.doors} Doors</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <FaCogs className="text-[#ca8032]" />
                        <span className="text-sm font-medium truncate">{car.transmission}</span>
                      </div>
                    </div>

                    <button className="w-full mt-auto py-3 bg-[#ca8032] hover:bg-[#b06d28] text-white font-bold rounded-lg transition-colors shadow-md"
                    onClick={()=>navigate("/vehicles")}>
                      Book Now
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          /* EMPTY STATE FIX: Displays when no cars match the filter */
          <div className="flex flex-col items-center justify-center py-20 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-sm">
            <div className="bg-white/10 p-6 rounded-full mb-4">
              <FaCarSide className="text-white/40 text-5xl" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-2">No vehicles found</h3>
            <p className="text-gray-300 mb-6 text-center max-w-md">
              We currently don't have any {activeFilter} models in our fleet. 
              Please check back soon or try another category.
            </p>
            <button 
              onClick={() => setActiveFilter("All")}
              className="px-6 py-2 bg-white text-[#004d2e] font-bold rounded-full hover:bg-[#ca8032] hover:text-white transition-all"
            >
              View All Vehicles
            </button>
          </div>
        )}

        {/* Navigation Arrows */}
        {filteredCars.length > 0 && (
          <>
            <button className="fleet-prev absolute left-[-15px] top-[40%] z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-[#ca8032] opacity-0 group-hover:opacity-100 transition-opacity">❮</button>
            <button className="fleet-next absolute right-[-15px] top-[40%] z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center text-[#ca8032] opacity-0 group-hover:opacity-100 transition-opacity">❯</button>
          </>
        )}
      </div>
    </section>
  );
}

export default Fleet;